import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Paper, Stack, Chip, Button, IconButton,
  Table, TableHead, TableBody, TableRow, TableCell, CircularProgress,
  Tabs, Tab, Collapse, Alert, Divider,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import HelpOutlineIcon from "@mui/icons-material/InfoOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import StorageIcon from "@mui/icons-material/Storage";
import { fetchReportLog, clearReportLog, verifyReport, fetchSessions } from "../api/adminApi";

const fmt = (v) => (typeof v === "number" ? v.toLocaleString(undefined, { maximumFractionDigits: 2 }) : v);

// ---- Verification panel (shown when a report row is expanded) ----
function VerifyPanel({ logId }) {
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const run = useCallback(() => {
    setLoading(true); setErr(""); setRes(null);
    verifyReport(logId).then(setRes).catch(e => setErr(e.message)).finally(() => setLoading(false));
  }, [logId]);
  useEffect(() => { run(); }, [run]);

  if (loading) return <Box sx={{ p: 2 }}><CircularProgress size={18} /> Verifying against the database…</Box>;
  if (err) return <Alert severity="error" sx={{ m: 1 }}>{err}</Alert>;
  if (!res) return null;

  const Banner = () => {
    if (res.all_match === true)
      return <Alert icon={<CheckCircleIcon />} severity="success" sx={{ mb: 1 }}>
        Report data MATCHES the database — every measure reconciles exactly.</Alert>;
    if (res.all_match === false)
      return <Alert icon={<ErrorIcon />} severity="error" sx={{ mb: 1 }}>
        MISMATCH — at least one measure does not match a direct database query.</Alert>;
    return <Alert icon={<HelpOutlineIcon />} severity="info" sx={{ mb: 1 }}>
      Not directly comparable (e.g. AVG or a filtered report) — values shown for reference.</Alert>;
  };

  return (
    <Box sx={{ p: 1.5, bgcolor: "#fafbff" }}>
      <Banner />
      <Table size="small">
        <TableHead>
          <TableRow>
            {["Measure", "Report total (all groups)", "Database (direct)", "Match", "Note"].map(h => (
              <TableCell key={h} sx={{ fontWeight: 700, fontSize: 12 }}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {res.results.map((r, i) => (
            <TableRow key={i}>
              <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{r.measure}</TableCell>
              <TableCell align="right" sx={{ fontFamily: "monospace", fontSize: 12 }}>{fmt(r.report_total)}</TableCell>
              <TableCell align="right" sx={{ fontFamily: "monospace", fontSize: 12 }}>{fmt(r.db_total)}</TableCell>
              <TableCell>
                {r.match === true && <Chip size="small" color="success" label="match" />}
                {r.match === false && <Chip size="small" color="error" label="mismatch" />}
                {r.match === null && <Chip size="small" variant="outlined" label="n/a" />}
              </TableCell>
              <TableCell sx={{ fontSize: 11, color: "#666" }}>{r.note}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Button size="small" startIcon={<RefreshIcon />} onClick={run} sx={{ mt: 1 }}>Re-verify</Button>
    </Box>
  );
}

// ---- One report-log row ----
function LogRow({ e }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <TableRow hover sx={{ "& td": { borderBottom: open ? "none" : undefined } }}>
        <TableCell sx={{ width: 30 }}>
          <IconButton size="small" onClick={() => setOpen(o => !o)}>
            {open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontSize: 12, whiteSpace: "nowrap" }}>{e.timestamp.replace("T", " ")}</TableCell>
        <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{e.fact_table}</TableCell>
        <TableCell sx={{ fontSize: 12 }}>
          {e.measures.map(m => m.label).join(", ")}
        </TableCell>
        <TableCell sx={{ fontSize: 12 }}>{e.dimensions.map(d => d.column).join(", ")}</TableCell>
        <TableCell><Chip size="small" label={e.agg} sx={{ height: 18, fontSize: 10 }} /></TableCell>
        <TableCell align="right" sx={{ fontFamily: "monospace", fontSize: 12 }}>{e.rows}</TableCell>
        <TableCell align="right" sx={{ fontFamily: "monospace", fontSize: 12 }}>{e.exec_ms} ms</TableCell>
        <TableCell>
          <Button size="small" variant="outlined" startIcon={<FactCheckIcon sx={{ fontSize: 15 }} />}
            onClick={() => setOpen(true)}>Verify</Button>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={9} sx={{ py: 0, borderBottom: open ? undefined : "none" }}>
          <Collapse in={open} unmountOnExit>
            <Box sx={{ my: 1 }}>
              <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 0.5 }}>
                Generated SQL {e.endpoint === "multi-query-pivot" ? "(table/pivot)" : "(chart)"}
              </Typography>
              <Box component="pre" sx={{ fontFamily: "monospace", fontSize: 11, bgcolor: "#1e2433", color: "#cfe0f5",
                p: 1.5, borderRadius: 1, overflow: "auto", whiteSpace: "pre-wrap", wordBreak: "break-all", my: 0.5 }}>
                {e.sql}
              </Box>
              <Divider sx={{ my: 1 }} />
              <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, mb: 0.5 }}>
                Independent verification — report vs. direct database
              </Typography>
              <VerifyPanel logId={e.id} />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// ---- Report Log tab ----
function ReportLogTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const load = () => {
    setLoading(true);
    fetchReportLog().then(setData).catch(e => setErr(e.message)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Typography sx={{ fontWeight: 700 }}>Report Log</Typography>
        <Chip size="small" label={`${data?.count ?? 0} runs`} />
        <Box sx={{ flex: 1 }} />
        <Button size="small" startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
        <Button size="small" color="error" startIcon={<DeleteSweepIcon />}
          onClick={() => clearReportLog().then(load)}>Clear</Button>
      </Stack>
      {loading ? <Box sx={{ p: 3, textAlign: "center" }}><CircularProgress size={22} /></Box>
        : err ? <Alert severity="error">{err}</Alert>
        : !data?.entries?.length ? (
          <Alert severity="info">No reports run yet. Build and run a report in the Report Builder, then refresh here.</Alert>
        ) : (
          <Paper variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f3f6fc" }}>
                  {["", "Time", "Fact", "Measures", "Dimensions", "Agg", "Rows", "Exec", "Verify"].map((h, i) => (
                    <TableCell key={i} sx={{ fontWeight: 700, fontSize: 12 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.entries.map(e => <LogRow key={e.id} e={e} />)}
              </TableBody>
            </Table>
          </Paper>
        )}
    </Box>
  );
}

// ---- Sessions tab ----
function SessionsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const load = () => {
    setLoading(true);
    fetchSessions().then(setData).catch(e => setErr(e.message)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Typography sx={{ fontWeight: 700 }}>Session Management</Typography>
        <Box sx={{ flex: 1 }} />
        <Button size="small" startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
      </Stack>
      {loading ? <Box sx={{ p: 3, textAlign: "center" }}><CircularProgress size={22} /></Box>
        : err ? <Alert severity="error">{err}</Alert>
        : (
          <>
            {data.pool && !data.pool.error && (
              <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
                {[["Pool open", data.pool.opened], ["Busy", data.pool.busy],
                  ["Min", data.pool.min], ["Max", data.pool.max]].map(([k, v]) => (
                  <Paper key={k} variant="outlined" sx={{ px: 2, py: 1, textAlign: "center", minWidth: 90 }}>
                    <Typography sx={{ fontSize: 10, color: "#888", textTransform: "uppercase", fontWeight: 700 }}>{k}</Typography>
                    <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#2563eb" }}>{v}</Typography>
                  </Paper>
                ))}
              </Stack>
            )}
            <Typography sx={{ fontSize: 12, color: "#666", mb: 1 }}>
              {data.session_count} active Oracle session(s) for the application user.
            </Typography>
            <Paper variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f3f6fc" }}>
                    {["SID", "Serial#", "Status", "Program", "Machine", "Logon time"].map(h => (
                      <TableCell key={h} sx={{ fontWeight: 700, fontSize: 12 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.sessions.map((s, i) => (
                    <TableRow key={i} hover>
                      <TableCell sx={{ fontSize: 12 }}>{s.sid}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{s.serial}</TableCell>
                      <TableCell>
                        <Chip size="small" label={s.status}
                          color={s.status === "ACTIVE" ? "success" : "default"} sx={{ height: 18, fontSize: 10 }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: 11, fontFamily: "monospace" }}>{s.program}</TableCell>
                      <TableCell sx={{ fontSize: 11, fontFamily: "monospace" }}>{s.machine}</TableCell>
                      <TableCell sx={{ fontSize: 12, whiteSpace: "nowrap" }}>{s.logon_time}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </>
        )}
    </Box>
  );
}

// ---- Page ----
export default function Admin() {
  const [tab, setTab] = useState(0);
  return (
    <Box sx={{ bgcolor: "#eef0f5", minHeight: "100vh", py: 3 }}>
      <Box sx={{ maxWidth: 1180, mx: "auto", px: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <StorageIcon sx={{ color: "#2563eb" }} />
          <Typography variant="h6" sx={{ fontWeight: 800 }}>Administration</Typography>
        </Stack>
        <Typography variant="body2" sx={{ color: "#666", mb: 2 }}>
          Every report run is logged with its generated SQL, timestamp and execution time. Expand a row to
          verify the report's totals against an independent, direct database query — so you can confirm the
          numbers are correct, not just rendered.
        </Typography>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
            <Tab label="Report Log & Verification" />
            <Tab label="Session Management" />
          </Tabs>
          {tab === 0 ? <ReportLogTab /> : <SessionsTab />}
        </Paper>
      </Box>
    </Box>
  );
}
